/**
 * Breadcrumb Navigation Component
 *
 * Renders visible breadcrumb trail + BreadcrumbList Schema.org structured data.
 * Helps Google crawl increased page volume without indexing errors.
 */

import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

const SITE_URL = 'https://rooflyzer.pro';

export const Breadcrumbs = ({ items, className = '' }: BreadcrumbsProps) => {
  // Build full list: Home + custom items
  const allItems: BreadcrumbItem[] = [{ name: 'Home', url: '/' }, ...items];

  // Generate BreadcrumbList schema
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: allItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };

  return (
    <>
      {/* Schema.org BreadcrumbList structured data */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Visible breadcrumb navigation */}
      <nav
        aria-label="Breadcrumb"
        className={`flex items-center gap-1.5 text-sm text-white/60 ${className}`}
      >
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;
          return (
            <span key={item.url} className="flex items-center gap-1.5">
              {index > 0 && <ChevronRight className="size-3.5 text-white/30 flex-shrink-0" />}
              {isLast ? (
                <span className="text-white/90 font-medium truncate max-w-[200px]">
                  {index === 0 ? <Home className="size-3.5" /> : item.name}
                </span>
              ) : (
                <Link
                  to={item.url}
                  className="hover:text-white transition-colors truncate max-w-[200px]"
                >
                  {index === 0 ? <Home className="size-3.5" /> : item.name}
                </Link>
              )}
            </span>
          );
        })}
      </nav>
    </>
  );
};

export default Breadcrumbs;
